<script>
import baseMixin from '@/mixins/baseMixin'
import dayjs from '@/services/date/index'
import DateFormatToken from '@/shared/constants/date/DateFormatToken'
import {
  CalendarType,
  ActiveDatePicker,
  Conditions,
  CalendarView,
  Days,
  Months,
} from '@/shared/constants/datePicker/picker'

export default {
  name: 'DatePicker',
  mixins: [baseMixin],
  props: {
    value: { type: [Array, String, Date], default: () => [] }, // [startDate, endDate]
    startDate: { type: [String, Date], default: null },
    endDate: { type: [String, Date], default: null },
    calendarType: { type: String, default: '' },
    condition: { type: String, default: Conditions.IS },
  },
  emits: [
    'input',
    'update-condition',
    'confirm-date-filter',
    'clear-fields',
    'close-date-filter',
  ],
  data() {
    const today = new Date()
    let initialDate = today
    if (this.calendarType === CalendarType.RANGE) {
      if (Array.isArray(this.value) && this.value[0]) {
        initialDate = new Date(this.value[0])
      }
    } else if (this.startDate) {
      initialDate = new Date(this.startDate)
    }

    return {
      CalendarType,
      CalendarView,
      Conditions,
      ActiveDatePicker,
      startLabel: 'Start date',
      localCondition: this.condition,
      conditions: Object.values(Conditions),
      activePicker: ActiveDatePicker.START,
      today,
      currentMonth: initialDate.getMonth(),
      currentYear: initialDate.getFullYear(),
      selectedDate: new Date(today),
      localStart: this.startDate ? new Date(this.startDate) : null,
      localEnd: this.endDate ? new Date(this.endDate) : null,
      view: CalendarView.CALENDAR,
      days: Object.values(Days),
      monthNames: Object.values(Months),
    }
  },
  computed: {
    years() {
      return Array.from({ length: 100 }, (_, i) => 1990 + i)
    },
    calendarDays() {
      const days = []
      const firstDay = new Date(this.currentYear, this.currentMonth, 1)
      const startDay = firstDay.getDay()
      const daysInMonth = new Date(
        this.currentYear,
        this.currentMonth + 1,
        0
      ).getDate()
      const prevMonthDays = new Date(
        this.currentYear,
        this.currentMonth,
        0
      ).getDate()

      // Previous month padding
      for (let i = startDay - 1; i >= 0; i--) {
        days.push(
          new Date(this.currentYear, this.currentMonth - 1, prevMonthDays - i)
        )
      }

      // Current month
      for (let i = 1; i <= daysInMonth; i++) {
        days.push(new Date(this.currentYear, this.currentMonth, i))
      }

      // Next month padding
      while (days.length % 7 !== 0) {
        days.push(
          new Date(this.currentYear, this.currentMonth + 1, days.length % 7)
        )
      }

      return days
    },
    activePickerValue: {
      get() {
        return this.activePicker === ActiveDatePicker.START
          ? this.localStart
          : this.localEnd
      },
      set(val) {
        if (this.activePicker === ActiveDatePicker.START) {
          this.localStart = val
        } else {
          this.localEnd = val
        }

        if (
          this.localCondition === Conditions.BETWEEN &&
          this.activePicker === ActiveDatePicker.START
        ) {
          this.activePicker = ActiveDatePicker.END
        }
      },
    },
    minDate() {
      return this.localCondition === Conditions.AFTER ||
        this.activePicker === ActiveDatePicker.END
        ? this.startDate
        : null
    },
    maxDate() {
      return this.localCondition === Conditions.BEFORE ? this.localStart : null
    },
    displayedStartDate() {
      return this.calendarType !== CalendarType.RANGE
        ? this.localStart
          ? this.formatCalenderDate(this.localStart)
          : ''
        : this.value[0]
        ? this.formatCalenderDate(this.value[0])
        : ''
    },
    displayedEndDate() {
      return this.calendarType !== CalendarType.RANGE
        ? this.localCondition === Conditions.BETWEEN && this.localEnd
          ? this.formatCalenderDate(this.localEnd)
          : ''
        : this.localCondition === Conditions.BETWEEN && this.value[1]
        ? this.formatCalenderDate(this.value[1])
        : ''
    },
    isRangeMode() {
      return (
        this.calendarType === CalendarType.RANGE ||
        this.localCondition === Conditions.BETWEEN
      )
    },
  },
  watch: {
    startDate(newVal) {
      this.localStart = newVal ? new Date(newVal) : null
    },
    endDate(newVal) {
      this.localEnd = newVal ? new Date(newVal) : null
    },
    condition(newVal) {
      this.localCondition = newVal
    },
    value: {
      immediate: true,
      handler(newVal) {
        if (this.calendarType === CalendarType.RANGE) {
          if (Array.isArray(newVal)) {
            this.localStart = newVal[0] ? new Date(newVal[0]) : null
            this.localEnd = newVal[1] ? new Date(newVal[1]) : null
          }
        }
      },
    },
    view(newView) {
      if (newView === CalendarView.YEAR) {
        this.$nextTick(() => {
          const selectedElement = this.$el.querySelector(
            '.year-scroll .selected'
          )
          if (
            selectedElement &&
            typeof selectedElement.scrollIntoView === 'function'
          ) {
            selectedElement.scrollIntoView({ block: 'center' })
          }
        })
      }
    },
  },
  methods: {
    onConditionChange(val) {
      this.localCondition = val
      this.$emit('update-condition', val) // custom event
    },
    formatCalenderDate(date) {
      if (!date) return ''
      const d = new Date(date)
      return d.toLocaleDateString(this.$i18n.locale, {
        day: '2-digit',
        month: '2-digit',
        year: '2-digit',
      })
    },
    prevMonth() {
      if (this.currentMonth === 0) {
        this.currentMonth = 11
        this.currentYear--
      } else {
        this.currentMonth--
      }
    },
    nextMonth() {
      if (this.currentMonth === 11) {
        this.currentMonth = 0
        this.currentYear++
      } else {
        this.currentMonth++
      }
    },
    selectMonth(month) {
      this.currentMonth = month
      this.view = CalendarView.CALENDAR
    },
    selectYear(year) {
      this.currentYear = year
      this.view = CalendarView.CALENDAR
    },
    selectDate(date) {
      this.selectedDate = new Date(date)
      this.currentMonth = date.getMonth()
      this.currentYear = date.getFullYear()
      this.onDatePick(this.selectedDate)
    },
    isSameDate(d1, d2) {
      return (
        d1?.getDate() === d2?.getDate() &&
        d1?.getMonth() === d2?.getMonth() &&
        d1?.getFullYear() === d2?.getFullYear()
      )
    },
    isBetween(date) {
      return (
        this.localStart &&
        this.localEnd &&
        date > this.localStart &&
        date < this.localEnd
      )
    },
    getDayClassRange(date) {
      const isStart = this.isSameDate(date, this.localStart)
      const isEnd = this.isSameDate(date, this.localEnd)
      const isSingle = isStart && isEnd

      return {
        today: this.isSameDate(date, this.today),
        start: isStart,
        end: isEnd,
        between: this.isBetween(date),
        'other-month': date.getMonth() !== this.currentMonth,
        'start-end': isSingle, // optional class if you want special handling
      }
    },
    // common
    clearFields() {
      if (this.calendarType !== CalendarType.RANGE) {
        this.localStart = null
        this.localEnd = null
        this.activePicker = ActiveDatePicker.START
      }
      this.$emit('clear-fields')
    },
    // for normal calendar
    isSelected(date) {
      return (
        this.selectedDate.getDate() === date.getDate() &&
        this.selectedDate.getMonth() === date.getMonth() &&
        this.selectedDate.getFullYear() === date.getFullYear()
      )
    },
    getDayClass(date) {
      return {
        today: this.isSameDate(date, this.today),
        selected: this.isSelected(date),
        'other-month': date.getMonth() !== this.currentMonth,
      }
    },
    confirmDateFilter() {
      const format = (d) =>
        d ? dayjs(d).format(DateFormatToken.YYYYMMDD) : null

      let start = null
      let end = null

      switch (this.localCondition) {
        case Conditions.IS:
          start = format(this.localStart)
          end = format(this.localStart)
          break
        case Conditions.BEFORE:
          start = null
          end = format(this.localStart)
          break
        case Conditions.AFTER:
          start = format(this.localStart)
          end = null
          break
        case Conditions.BETWEEN:
          start = format(this.localStart)
          end = format(this.localEnd)
          break
      }

      if (!this.localStart && !this.localEnd) {
        start = dayjs().format(DateFormatToken.YYYYMMDD)
        end = null
      }
      this.$emit('confirm-date-filter', { startDate: start, endDate: end })
    },
    onDatePick(val) {
      if (this.localCondition === Conditions.BETWEEN) {
        if (this.activePicker === ActiveDatePicker.START) {
          this.localStart = val
          this.activePicker = ActiveDatePicker.END
          this.localEnd = null
        } else if (this.activePicker === ActiveDatePicker.END) {
          if (this.localStart && dayjs(val).isBefore(dayjs(this.localStart))) {
            this.localStart = val
            this.localEnd = null

            this.activePicker = ActiveDatePicker.START
            this.$nextTick(() => {
              this.activePicker = ActiveDatePicker.END
            })
          } else {
            this.localEnd = val
          }
        }
      } else {
        this.localStart = val
        this.localEnd = null
      }
      this.$emit('input', [this.localStart, this.localEnd])
    },
  },
}
</script>

<template>
  <div
    class="custom-datepicker-popup"
    :class="{ 'date-filter-container': calendarType !== CalendarType.RANGE }"
  >
    <div class="date-selection-header">
      <div class="d-flex" style="justify-content: flex-start; width: 100%">
        <v-select
          v-model="startLabel"
          :items="['Start date']"
          style="margin-right: 10px"
          outlined
        />
        <v-select
          :id="'conditions'"
          :items="conditions"
          :value="localCondition"
          no-border
          outlined
          menu-props="{ contentClass: 'condition-dropdown' }"
          @input="onConditionChange"
        />
      </div>
      <div
        class="d-flex"
        style="justify-content: space-between; margin-bottom: 15px"
      >
        <div
          class="date-show-container"
          :class="{
            active: activePicker === ActiveDatePicker.START,
            'date-show-container-clickable':
              localCondition === Conditions.BETWEEN,
          }"
          @click="
            localCondition === Conditions.BETWEEN &&
              (activePicker = ActiveDatePicker.START)
          "
        >
          {{ displayedStartDate }}
        </div>
        <div
          class="date-show-container"
          :class="{
            disabled: localCondition !== Conditions.BETWEEN,
            active:
              activePicker === ActiveDatePicker.END &&
              localCondition === Conditions.BETWEEN,
            'date-show-container-clickable':
              localCondition === Conditions.BETWEEN,
          }"
        >
          {{ displayedEndDate }}
        </div>
      </div>
    </div>

    <v-divider style="margin-bottom: 10px"></v-divider>

    <div class="datepicker">
      <!-- Header -->
      <div class="datepicker-header">
        <div class="selectors">
          <span
            @click="
              view =
                view === CalendarView.MONTH
                  ? CalendarView.CALENDAR
                  : CalendarView.MONTH
            "
            >{{ monthNames[currentMonth] }}</span
          >
          <span
            @click="
              view =
                view === CalendarView.YEAR
                  ? CalendarView.CALENDAR
                  : CalendarView.YEAR
            "
            >{{ currentYear }}</span
          >
          <v-icon
            size="20"
            class="mb-1"
            @click="
              view =
                view === CalendarView.YEAR
                  ? CalendarView.CALENDAR
                  : CalendarView.YEAR
            "
            >mdi-menu-down</v-icon
          >
        </div>
        <div class="nav-buttons">
          <BaseButton
            xs
            width="10"
            color="white"
            text-color="grey"
            hover-color="transparent"
            @click="prevMonth"
          >
            <v-icon size="20">mdi-chevron-left</v-icon>
          </BaseButton>
          <BaseButton
            xs
            width="10"
            color="white"
            text-color="grey"
            hover-color="transparent"
            @click="nextMonth"
          >
            <v-icon size="20">mdi-chevron-right</v-icon>
          </BaseButton>
        </div>
      </div>

      <!-- Calendar View -->
      <div
        v-if="view === CalendarView.CALENDAR"
        :class="{ calendar: calendarType !== CalendarType.RANGE }"
      >
        <div class="day-names">
          <div v-for="day in days" :key="`calendar-day-name-${day}`">{{
            day
          }}</div>
        </div>
        <div
          class="days"
          :class="{
            'has-range': isRangeMode && localStart && localEnd,
          }"
        >
          <div
            v-for="(date, index) in calendarDays"
            :key="`calendar-day-${index}`"
            :data-day="`calendar-day-${date.getDate()}`"
            :class="[
              isRangeMode ? 'day' : 'day-of-date',
              isRangeMode ? getDayClassRange(date) : getDayClass(date),
            ]"
            @click="selectDate(date)"
          >
            {{ date.getDate() }}
          </div>
        </div>
      </div>

      <!-- Month View -->
      <div v-if="view === CalendarView.MONTH" class="month-grid">
        <div
          v-for="(month, index) in monthNames"
          :key="`calendar-month-${index}`"
          :class="{ selected: index === currentMonth }"
          @click="selectMonth(index)"
        >
          {{ month }}
        </div>
      </div>

      <!-- Year View -->
      <div v-if="view === CalendarView.YEAR" class="year-scroll">
        <div
          v-for="year in years"
          :key="`calendar-year-${year}`"
          :class="{ selected: year === currentYear }"
          @click="selectYear(year)"
        >
          {{ year }}
        </div>
      </div>
    </div>

    <v-divider></v-divider>

    <v-card-actions class="justify-space-between px-4">
      <BaseButton
        text
        xs
        width="10"
        class="date-picker-btns"
        @click="clearFields"
      >
        {{ $t('general.clear') }}
      </BaseButton>
      <v-spacer></v-spacer>
      <BaseButton
        text
        xs
        width="10"
        class="date-picker-btns"
        @click="$emit('close-date-filter')"
      >
        {{ $t('general.cancel') }}
      </BaseButton>
      <BaseButton
        text
        xs
        width="10"
        :class="['date-picker-ok-btn', colorClass.text.blue400]"
        @click="
          () => {
            calendarType !== CalendarType.RANGE
              ? confirmDateFilter()
              : $emit('confirm-date-filter')
          }
        "
      >
        {{ $t('exportBookings.buttons.ok') }}
      </BaseButton>
    </v-card-actions>
  </div>
</template>

<style lang="scss">
@import '@/styles/_colors.scss';
@import '@/styles/bookingList/date-picker.scss';
</style>
